/**
 * 63.1d step 2 (lane F): a driving body's seat on the ground — its frame pitched along the ground under its axles and
 * rolled across the ground under its sides, so the drawn hull rests on its wheels on a slope and its hit shape stands where
 * it is drawn (the coordinator: "the hit shape and the drawn hull must share the frame", as the structures share
 * math.ts uprightFrame). Its axes: `front` the drive heading laid on the ground, `left` = up × front, `up` = front × left;
 * `foot` the ground point under the hull's middle, the model's origin.
 *
 * The ground is the person's (core/fineTerrain.ts personSurfaceRadius — what the wheels and the walk read), probed half a
 * wheelbase ahead and behind and half a track to either side (DriveSpec, people). A bike has no track: it is pitched
 * only, its lean into a turn drawn on top. Deterministic (arithmetic and the ground query). The host passes its drawn
 * ground (`groundAt`), as it stands drawn people on it: the fine ground near the camera with the patch on, so there the
 * drawn hull and the hit shape are one.
 */
import type { PlanetState, StructureState, Vec3 } from '../types';
import { vecAdd, vecScale, vecSub, vecNormalize, vecCross, vecDot, surfaceTangentFrame } from '../math';
import { personSurfaceRadius, type GroundDials } from './fineTerrain';
import type { PersonSet } from './scaleProfile';
import { driveSpec } from './drive';

export interface VehicleFrame { foot: Vec3; up: Vec3; front: Vec3; left: Vec3 }

export function vehicleFrame(planet: PlanetState, s: Pick<StructureState, 'type' | 'surfaceDir' | 'drive'>, person: number, ps: GroundDials, groundAt?: (dir: Vec3) => number): VehicleFrame {
  const spec = driveSpec(s.type);
  const n = vecNormalize(s.surfaceDir);
  const h = s.drive?.heading ?? surfaceTangentFrame(n).tx;
  const front0 = vecNormalize(vecSub(h, vecScale(n, vecDot(h, n))));
  const left0 = vecCross(n, front0);
  /** The ground point `along` people ahead and `across` people to the left of the body's spot. */
  const ground = (along: number, across: number): Vec3 => {
    const d = vecNormalize(vecAdd(n, vecAdd(vecScale(front0, (along * person) / planet.radius), vecScale(left0, (across * person) / planet.radius))));
    return vecAdd(planet.position, vecScale(d, groundAt ? groundAt(d) : personSurfaceRadius(planet, d, ps)));
  };
  const half = spec.wheelbase / 2, side = spec.track / 2;
  const pf = ground(half, 0), pr = ground(-half, 0);
  const front = vecNormalize(vecSub(pf, pr));
  let across = left0;
  let foot = vecScale(vecAdd(pf, pr), 0.5);
  if (side > 0) {
    const pl = ground(0, side), pq = ground(0, -side);
    across = vecNormalize(vecSub(pl, pq));
    foot = vecScale(vecAdd(foot, vecScale(vecAdd(pl, pq), 0.5)), 0.5);
  }
  const up = vecNormalize(vecCross(front, across));
  const left = vecCross(up, front);
  return { foot, up, front, left };
}
